import { useContext, useState } from 'react';
import {
  AgxButton,
  Breakpoints,
  AgxSpacer,
  AgxTypography,
  Icon_SparkleForm,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { FormConfigType, SPECIAL_CHARACTERS, StakeholderType } from '@urbanx/agx-ui-components';
import { setAndShowErrorToast } from 'store/config';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useFormLoader } from 'hooks/useFormLoader';
import { Tabs } from 'types/Tabs';
import { completeCampaignStep } from 'Api/Campaigns/campaignsApi';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { formatListOfStrings } from 'utils/formatString';
import { getAllBuyerNames, prepareSignatories } from 'utils/vendorBuyerUtil';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { AuthContext } from '@urbanx/agx.auth.client';
import {
  CampaignDetailModel,
  ContractDetailModel,
  InProgressFormModelFormType,
} from '@urbanx/agx.campaigns.client';

interface ResumeContractSubmissionProps {
  campaign: CampaignDetailModel;
  contract: ContractDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
}

const ResumeContractSubmission = ({
  campaign,
  contract,
  mobile = false,
}: ResumeContractSubmissionProps) => {
  const { address, campaignId } = campaign;

  const screenSize = useWindowSize();
  const isMobile = screenSize === Breakpoints.Mobile;
  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm, loadFormWithConfig } = useFormLoader();
  const [disableButtons, setDisableButtons] = useState(false);
  const shouldStartSubmission =
    contract.inProgressForm?.formType === InProgressFormModelFormType.ContractExchange;

  const resumeContractSubmission = async () => {
    try {
      setDisableButtons(true);

      if (shouldStartSubmission) {
        const authToken = await getAuthToken();

        if (!authToken) return;

        const newForm = await completeCampaignStep(
          authToken,
          campaignId ?? '',
          false,
          contract.contractId
        );

        if (!newForm) {
          dispatch(setAndShowErrorToast('Failed to resume Contract submission'));
          return;
        }

        await loadFormWithConfig(newForm);
        return;
      }

      await loadForm(campaignId ?? '', contract.contractId ?? '', FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to resume Contract submission'));
    } finally {
      setDisableButtons(false);
    }
  };
  const allBuyers = prepareSignatories(contract.buyers, StakeholderType.Buyer);
  const allBuyerNames = getAllBuyerNames(allBuyers);

  return (
    <>
      <AgxSpacer direction="vertical" gap="spacing-xl" contentAlignment="center">
        <AgxTypography
          id="resume-contract-submission-address"
          type="heading"
          size="2x-large"
          style={{ textAlign: 'center' }}
        >
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxTypography>
        <AgxTypography
          id="resume-contract-submission-buyers-sale-price"
          type="body"
          size="medium"
          style={{ textAlign: 'center' }}
        >
          {formatListOfStrings(allBuyerNames)} {SPECIAL_CHARACTERS.BULLET}{' '}
          {formatCurrencyWithConditionalDecimals(contract.salePrice ?? 0, 2)}
        </AgxTypography>
        <AgxSpacer direction="vertical" gap="xs" contentAlignment="center">
          <AgxTypography
            id="resume-contract-submission-more-fields"
            type="body"
            size="medium"
            style={{ textAlign: 'center' }}
          >
            There are more fields to fill,
          </AgxTypography>
          <AgxTypography
            id="resume-contract-submission-continue"
            type="body"
            size="medium"
            style={{ textAlign: 'center' }}
          >
            let's continue from where you left off
          </AgxTypography>
        </AgxSpacer>
        <Icon_SparkleForm width={isMobile ? 150 : 200} height={isMobile ? 150 : 200} />
      </AgxSpacer>
      <AgxButton
        id="btn-resumeSubmission"
        variant={'hollow'}
        size="large"
        text="Resume Submission"
        onClick={resumeContractSubmission}
        disabled={disableButtons}
      />
    </>
  );
};

export default ResumeContractSubmission;
